import React, { useEffect, useState } from 'react';
import { Box, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { ResourceUsage, ResourceUsageSummary } from '@/types/resource-usage';

type ColorFamily = 'info' | 'secondary' | 'success' | 'warning' | 'primary' | 'error';
type Shade = 'light' | 'main' | 'dark';

const formatBytes = (bytes: number): string => bytes > 0 ? `${(bytes / 1024 / 1024).toFixed(1)} МБ` : '0 МБ';
const formatCount = (count: number): string => String(count);

// Та сама палітра, що в ResourceUsageCell.tsx — щоб колір категорії був однаковий
// і в барах, і в секторах кругової діаграми.
const CATEGORY_META: Record<keyof ResourceUsage, { label: string; format: (v: number) => string; colorFamily: ColorFamily; shade: Shade }> = {
  files_manager: { label: 'Файли', format: formatBytes, colorFamily: 'info', shade: 'main' },
  files_chat: { label: 'Файли в чаті', format: formatBytes, colorFamily: 'info', shade: 'dark' },
  chat_messages: { label: 'Повідомлення', format: formatCount, colorFamily: 'success', shade: 'main' },
  calendar_events: { label: 'Події', format: formatCount, colorFamily: 'warning', shade: 'main' },
  meetings: { label: 'Зустрічі', format: formatCount, colorFamily: 'secondary', shade: 'main' },
  ai_reports: { label: 'AI-звіти', format: formatCount, colorFamily: 'success', shade: 'dark' },
  notifications: { label: 'Сповіщення', format: formatCount, colorFamily: 'primary', shade: 'main' },
  expert_posts: { label: 'Пости', format: formatCount, colorFamily: 'secondary', shade: 'dark' },
  body_measurements: { label: 'Метрики', format: formatCount, colorFamily: 'warning', shade: 'dark' },
};

interface Props {
  id: number;
  fetchUsage: (id: number) => Promise<ResourceUsageSummary>;
}

/**
 * Компактна кругова діаграма — співвідношення використання ресурсів між собою.
 * Категорії мають різні одиниці (байти / кількість), тому частка кожної категорії
 * рахується відносно її власного threshold — інакше байти файлів завжди "забивали" б
 * кількість повідомлень/подій на діаграмі.
 * Сама довантажує дані незалежно за допомогою переданого `fetchUsage` (та сама архітектура,
 * що ResourceUsageCell — дозволяє перевикористання і в адмінці, і на сторінці клієнта).
 */
export const ResourceUsagePieChart: React.FC<Props> = ({ id, fetchUsage }) => {
  const theme = useTheme();
  const [summary, setSummary] = useState<ResourceUsageSummary | null>(null);

  useEffect(() => {
    setSummary(null);
    fetchUsage(id).then(setSummary).catch(() => {});
  }, [id, fetchUsage]);

  if (!summary) {
    return <Box sx={{ width: 36, height: 36 }} />;
  }

  const usage = summary.categories;

  const keys = Object.keys(CATEGORY_META) as (keyof ResourceUsage)[];
  const shares = keys.map((key) => usage[key].value / usage[key].threshold);
  const total = shares.reduce((sum, s) => sum + s, 0);

  const data = total > 0
    ? keys.map((key, i) => ({ key, value: shares[i] }))
    : [{ key: 'empty', value: 1 }];

  const tooltipText = keys
    .map((key) => `${CATEGORY_META[key].label}: ${CATEGORY_META[key].format(usage[key].value)}`)
    .join(' · ');

  return (
    <Tooltip title={tooltipText}>
      <Box sx={{ width: 36, height: 36 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              innerRadius={10}
              outerRadius={17}
              startAngle={90}
              endAngle={-270}
              stroke="none"
              isAnimationActive={false}
            >
              {data.map((entry) => {
                const meta = total > 0 ? CATEGORY_META[entry.key as keyof ResourceUsage] : null;
                const fill = meta ? theme.palette[meta.colorFamily][meta.shade] : theme.palette.action.disabledBackground;
                return <Cell key={entry.key} fill={fill} />;
              })}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </Box>
    </Tooltip>
  );
};
